import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@panel/commons/hooks';
import SnackBarContainer from '@panel/commons/lib/SimpleSnackbar/SnackBarContainer';
import { getDevice, isSiteWithTelBanner } from '@panel/commons/helpers';
import ErrorScreen from '@panel/commons/lib/ErrorScreen/ErrorScreen';
import Loader from '@panel/commons/lib/SimpleLoader';
import PostingMiniCard from '@panel/commons/lib/PostingMiniCard';
import Tabs from '@panel/commons/lib/Tabs';
import { userInfoSelectors } from '@panel/core/src/state/ducks/user';
import { CollaboratorsSelectors, CollaboratorsOperations } from '../../ducks/collaborators';
import { ContactDataSelectors, ContactDataOperations } from '../../ducks/contact-data';
import PageHeader from '../PageHeader';
import Collaborators from '../Collaborators';
import ContactData from '../ContactData';
import ButtonPanel from '../ButtonPanel';
import Skeleton from '../Skeleton';
import { Modal } from '../Modals/ModalWrapper';
import { ContainerLayout, LeftBar, Main, LoaderWrapper } from './styles';

const ContactosPermisosContainer = () => {
  const [tabLocation] = useQuery(useLocation(), ['tab']);
  const [currentTab, setCurrentTab] = useState(tabLocation || 'collaborators');
  const [activeLoader, setActiveLoader] = useState(false);
  const [disabledSkeleton, setDisabledSkeleton] = useState(false);
  const [finishedAction, setFinishedAction] = useState({ action: '', result: '' });
  const device = getDevice();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const tabsOptions = ['collaborators', 'contactsData'];
  const userInfo = useSelector(userInfoSelectors.getuserInfoState);
  const loadingUserInfo = userInfo && userInfo.isLoading;
  const { posting, loadingPostingInfo, postingError, loadingCollaborators, errorCollaborators } = useSelector(
    CollaboratorsSelectors.getCollaboratorsState
  );
  const { loadingPostingContactData, errorGetPostingContactData, errorUpdatingUserResponsible } = useSelector(
    ContactDataSelectors.getContactDataSelectors
  );
  const errorState = postingError || errorCollaborators || errorGetPostingContactData || errorUpdatingUserResponsible;

  useEffect(() => {
    // Se realiza fetch para traer datos de la mini card del aviso:
    dispatch(CollaboratorsOperations.getPostingInfo(postingId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *  La función añade el query params con el tab seleccionado.
   * @param {string} newTab - Nombre de la sección (tab) seleccionada.
   */
  const setQueryTab = newTab => {
    const params = new URLSearchParams(window.location.search);
    if (tabLocation !== newTab) {
      params.set('tab', newTab);
    }
    history.replace({ search: params.toString() });
  };

  /**
   * La función permite ir a la sección seleccionada.
   * @param {string} tab - Nombre de la sección a donde se quiere ir.
   * @returns - Retorna el componente a renderizar.
   */
  const getSectionContent = tab => {
    if (tab === 'contactsData') {
      return (
        <ContactData
          device={device}
          postingId={postingId}
          userInfo={userInfo}
          setActiveLoader={setActiveLoader}
          setDisabledSkeleton={setDisabledSkeleton}
          setFinishedAction={setFinishedAction}
        />
      );
    }
    return (
      <Collaborators
        device={device}
        postingId={postingId}
        userInfo={userInfo}
        setActiveLoader={setActiveLoader}
        setDisabledSkeleton={setDisabledSkeleton}
        setFinishedAction={setFinishedAction}
      />
    );
  };

  /**
   * La función valida, según el tab seleccionado, el loading del estado de cada sección (se usará para renderizar los skeletons)
   * @returns se devuelve un booleano
   */
  const getLoadingState = () => {
    if (disabledSkeleton) return false;
    if (currentTab === 'contactsData') return loadingPostingContactData || loadingCollaborators || loadingUserInfo;
    return loadingCollaborators || loadingUserInfo;
  };

  const handleOnclickError = () => {
    dispatch(CollaboratorsOperations.getPostingInfo(postingId));
    dispatch(CollaboratorsOperations.getCollaboratorsInfo(postingId));
    dispatch(ContactDataOperations.getPostingContactData(postingId));
  };

  return (
    <>
      <SnackBarContainer action={finishedAction} deviceType={device !== 'mobile' ? 'desktop' : 'mobile'} />
      {activeLoader && !errorState && (
        <Modal backgroundColor='transparent'>
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        </Modal>
      )}
      {errorState && <ErrorScreen isDesktop handleOnclick={handleOnclickError} />}
      {!errorState && (
        <ContainerLayout device={device} portalWithTelBanner={isSiteWithTelBanner}>
          <LeftBar device={device}>
            <ButtonPanel device={device} posting={posting} />
            {device !== 'desktop' && (
              <PageHeader
                title={t('panel.contactosPermisos.section_title')}
                titleSize='22px'
                titleWeight='600'
                marginTop='60px'
                marginBottom='14px'
              />
            )}
            <PostingMiniCard device={device} posting={posting} isLoading={loadingPostingInfo} />
            <Tabs
              device={device}
              tabsOptions={tabsOptions}
              sectionName='contactosPermisos'
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              setQueryTab={setQueryTab}
            />
          </LeftBar>
          <Main>
            {!getLoadingState() ? (
              <>
                {device === 'desktop' && (
                  <PageHeader
                    title={t('panel.contactosPermisos.section_title')}
                    subtitle={t(`panel.contactosPermisos.section_subtitle.${currentTab}`)}
                    dataPanel={currentTab}
                  />
                )}
                {device !== 'desktop' && (
                  <PageHeader
                    subtitle={t(`panel.contactosPermisos.section_subtitle.${currentTab}`)}
                    dataPanel={currentTab}
                  />
                )}
                {getSectionContent(currentTab)}
              </>
            ) : (
              <Skeleton device={device} currentTab={currentTab} />
            )}
          </Main>
        </ContainerLayout>
      )}
    </>
  );
};

export default ContactosPermisosContainer;
